import { useState } from "react";
import { WalletCards } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import type { SubscriptionPlanType } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { Button } from "@/shared/rd";
import { PanelDrawer } from "../drawer-panel";
import { SubscriptionPaymentSettleForm } from "./SubscriptionPaymentSettleForm";

type Props = {
  storeId: string;
  /**
   * A PER_ORDER_CREDIT store has no schedule to settle — the same endpoint is
   * a wallet top-up there, and the copy has to say so.
   */
  planType?: SubscriptionPlanType;
  triggerVariant?: "text" | "icon" | "menu";
  triggerClassName?: string;
  triggerLabel?: string;
  onOpen?: () => void;
  onSuccess?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

/**
 * Record a payment / top up the wallet. A form with several fields, so it
 * opens in a drawer over the page; the submit sits in the drawer's bar.
 */
export function StorePaymentModal({
  storeId,
  planType,
  triggerVariant = "text",
  triggerClassName,
  triggerLabel,
  onOpen,
  onSuccess,
  isOpen: controlledOpen,
  onOpenChange,
  hideTrigger,
}: Props) {
  const t = useTranslations();
  const [internalOpen, setInternalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const isPerOrderCredit =
    planType === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT ||
    planType === "FREE" ||
    planType === "PREMIUM";

  const label =
    triggerLabel ??
    (isPerOrderCredit
      ? t(keys.admin.subscriptionWalletTopUpAction)
      : t(keys.admin.subscriptionPaymentAddAction));
  const formId = `store-payment-${storeId}`;
  const open = () => {
    setOpen(true);
    onOpen?.();
  };

  return (
    <>
      {hideTrigger ? null : triggerVariant === "icon" ? (
        <Button variant="ghost" iconOnly icon={WalletCards} title={label} className={triggerClassName} onClick={open} />
      ) : triggerVariant === "menu" ? (
        <Button variant="ghost" size="sm" className={triggerClassName} onClick={open}>
          {label}
        </Button>
      ) : (
        <Button size="sm" icon={WalletCards} className={triggerClassName} aria-label={label} title={label} onClick={open}>
          {label}
        </Button>
      )}
      <PanelDrawer
        open={isOpen}
        onClose={() => setOpen(false)}
        closeLabel={t(keys.common.close)}
        badge="currency"
        title={isPerOrderCredit ? t(keys.admin.subscriptionWalletTopUpTitle) : t(keys.admin.subscriptionPaymentAddTitle)}
        sub={isPerOrderCredit ? t(keys.admin.subscriptionWalletTopUpHint) : t(keys.admin.subscriptionPaymentAddHint)}
        width={520}
        actions={
          <Button type="submit" form={formId} variant="primary" size="sm" loading={busy} loadingLabel={t("admin.saving")}>
            {isPerOrderCredit ? t(keys.admin.subscriptionWalletTopUpAction) : t(keys.common.save)}
          </Button>
        }
      >
        <SubscriptionPaymentSettleForm
          storeId={storeId}
          planType={planType}
          compact
          defaultOpen
          hideToggle
          hideSubmit
          formId={formId}
          onBusyChange={setBusy}
          onSuccess={() => {
            setOpen(false);
            onSuccess?.();
          }}
        />
      </PanelDrawer>
    </>
  );
}
